import React, { Suspense, lazy } from 'react';
import * as Sentry from '@sentry/react';
import {
    createBrowserRouter,
    Navigate,
    RouteObject,
    useParams,
} from 'react-router-dom';
import Loader from 'Components/Loader';
import ScorecardLoader from '../loaders/ScorecardLoader';
import Users from '@/operator/components/users/index';
import EditUser from '@/operator/components/users/edit-users/index';
import Profile from 'app/Pages/ProfilePage/Profile';
import LoggedOut from '@/operator/pages/logout';
import CheckingPage from '@/operator/pages/auth/setup/checking';
import DonePage from '@/operator/pages/auth/setup/done';
import OnboardingPage from '@/operator/pages/auth/setup/onboarding/OnboardingPage';
import ProgressPage from '@/operator/pages/auth/setup/progress';
import BuildPage from '@/operator/pages/auth/setup/build/BuildPage';
import LoginAuthPage from '@/operator/pages/auth/login';
import SignIn from '@/operator/pages/auth/setup/signin';
import AcceptInvite from '@/operator/pages/acceptInvite/AcceptInvite';
import {
    ConnectionsPage,
    ConnectionSettings,
    AddNewConnections,
} from '@/operator/pages/connections-page';
import { appRoutes } from '@/routes/constants';

import AppLayout from '@/layouts/AppLayout';
import OperatorLayout from 'app/layouts/OperatorLayout';
import ErrorPage from '@/pages/ErrorPage';
import LoginPage from 'app/Pages/LoginPage';
import ResetPasswordPage from 'app/Pages/ResetPasswordPage';
import GoogleSignInLanding from '@/extension-signin/GoogleSignInLanding';
import { getAdminChildRoute } from './utils';

const SubdomainEnforcer = lazy(() => import('../SubdomainEnforcer'));
const RubricLoader = lazy(() => import('../loaders/RubricLoader'));
const DashboardPage = lazy(() => import('../Pages/DashboardPage'));
const TeamRosterPage = lazy(() => import('../Pages/TeamRosterPage'));
const TicketDetails = lazy(
    () => import('../Components/Dashboard/TicketDetails/TicketDetails'),
);
const SettingsPage = lazy(() => import('../Pages/SettingsPage'));
const ScorecardsPage = lazy(() => import('../Pages/ScorecardsPage'));
const AnalyticsPage = lazy(() => import('../Pages/AnalyticsPage'));
const QueuePage = lazy(() => import('../Pages/QueuePage'));
const SingleCalibrationView = lazy(
    () =>
        import(
            '../Containers/Calibrations/CalibrationDetails/SingleCalibrationView'
        ),
);
const CalibrationLanding = lazy(
    () =>
        import(
            '../Containers/Calibrations/CalibrationLanding/CalibrationLanding'
        ),
);
const CalibrationDetails = lazy(
    () => import('../Components/CalibrationDetails/index'),
);

const RedirectToHome = () => {
    const { orgId } = useParams();
    return <Navigate to={`/org/${orgId}/${appRoutes.HOME}`} replace />;
};

const routes: RouteObject[] = [
    {
        path: '/',
        index: true,
        element: <Navigate to="/org/app/home" replace />,
    },
    {
        path: 'org/:orgId',
        element: (
            <Suspense
                fallback={<Loader message="Getting everything ready for you" />}
            >
                <SubdomainEnforcer />
            </Suspense>
        ),
        children: [
            {
                path: '',
                errorElement: <ErrorPage />,
                element: <AppLayout />,
                children: [
                    {
                        path: 'quality/dashboard',
                        element: <RedirectToHome />,
                    },
                    {
                        path: appRoutes.HOME,
                        element: (
                            <Suspense
                                fallback={
                                    <Loader message="Getting everything ready for you" />
                                }
                            >
                                <ScorecardLoader>
                                    <DashboardPage />
                                </ScorecardLoader>
                            </Suspense>
                        ),
                    },
                    {
                        path: appRoutes.TEAM,
                        element: (
                            <Suspense
                                fallback={
                                    <Loader message="Getting everything ready for you" />
                                }
                            >
                                <TeamRosterPage />
                            </Suspense>
                        ),
                    },
                    {
                        path: appRoutes.TICKET,
                        element: (
                            <Suspense
                                fallback={
                                    <Loader message="Getting everything ready for you" />
                                }
                            >
                                <RubricLoader>
                                    <TicketDetails />
                                </RubricLoader>
                            </Suspense>
                        ),
                    },
                    {
                        path: appRoutes.SETTINGS,
                        element: (
                            <Suspense
                                fallback={
                                    <Loader message="Getting everything ready for you" />
                                }
                            >
                                <SettingsPage />
                            </Suspense>
                        ),
                    },
                    {
                        path: appRoutes.SCORECARDS,
                        element: (
                            <Suspense
                                fallback={
                                    <Loader message="Getting everything ready for you" />
                                }
                            >
                                <ScorecardsPage />
                            </Suspense>
                        ),
                    },
                    {
                        path: appRoutes.ANALYTICS,
                        element: (
                            <Suspense
                                fallback={
                                    <Loader message="Getting everything ready for you" />
                                }
                            >
                                <RubricLoader>
                                    <AnalyticsPage />
                                </RubricLoader>
                            </Suspense>
                        ),
                    },
                    {
                        path: appRoutes.QUEUE,
                        element: (
                            <Suspense
                                fallback={
                                    <Loader message="Getting everything ready for you" />
                                }
                            >
                                <QueuePage />
                            </Suspense>
                        ),
                    },
                    {
                        path: appRoutes.CALIBRATION,
                        element: (
                            <Suspense
                                fallback={
                                    <Loader message="Getting everything ready for you" />
                                }
                            >
                                <RubricLoader>
                                    <SingleCalibrationView />
                                </RubricLoader>
                            </Suspense>
                        ),
                    },
                    {
                        path: appRoutes.CALIBRATIONS_HOME,
                        element: (
                            <Suspense
                                fallback={
                                    <Loader message="Getting everything ready for you" />
                                }
                            >
                                <RubricLoader>
                                    <CalibrationLanding />
                                </RubricLoader>
                            </Suspense>
                        ),
                    },
                    {
                        path: appRoutes.CALIBRATION_REVIEW,
                        element: (
                            <Suspense
                                fallback={
                                    <Loader message="Getting everything ready for you" />
                                }
                            >
                                <RubricLoader>
                                    <CalibrationDetails />
                                </RubricLoader>
                            </Suspense>
                        ),
                    },
                    {
                        path: appRoutes.OPERATOR_PROFILE,
                        element: (
                            <Suspense
                                fallback={
                                    <Loader message="Getting everything ready for you" />
                                }
                            >
                                <Profile />
                            </Suspense>
                        ),
                    },
                    {
                        path: appRoutes.OPERATOR_ADMIN,
                        element: <OperatorLayout roles={[]} />,
                        children: [
                            {
                                path: getAdminChildRoute(
                                    appRoutes.OPERATOR_ADMIN_CONNECTIONS,
                                ),
                                element: <ConnectionsPage />,
                            },
                            {
                                path: getAdminChildRoute(
                                    appRoutes.OPERATOR_ADMIN_CONNECTIONS_ADD,
                                ),
                                element: <AddNewConnections />,
                            },
                            {
                                path: getAdminChildRoute(
                                    appRoutes.OPERATOR_ADMIN_CONNECTIONS_SETTINGS,
                                ),
                                element: <ConnectionSettings />,
                            },
                        ],
                    },
                    {
                        path: appRoutes.OPERATOR_USERS,
                        element: (
                            <Suspense
                                fallback={
                                    <Loader message="Getting everything ready for you" />
                                }
                            >
                                <Users />
                            </Suspense>
                        ),
                    },
                    {
                        path: appRoutes.OPERATOR_USER_EDIT,
                        element: (
                            <Suspense
                                fallback={
                                    <Loader message="Getting everything ready for you" />
                                }
                            >
                                <EditUser />
                            </Suspense>
                        ),
                    },
                ],
            },

            {
                path: '',
                element: <OperatorLayout secure />,
                errorElement: <ErrorPage />,
                children: [
                    {
                        path: appRoutes.AUTH_SETUP_BUILD,
                        element: (
                            <Suspense
                                fallback={<Loader message="Checking session" />}
                            >
                                <BuildPage />
                            </Suspense>
                        ),
                    },
                    {
                        path: appRoutes.AUTH_SETUP_PROGRESS,
                        element: (
                            <Suspense
                                fallback={<Loader message="Checking session" />}
                            >
                                <ProgressPage />
                            </Suspense>
                        ),
                    },
                    {
                        path: appRoutes.AUTH_SETUP_ONBOARDING,
                        element: (
                            <Suspense
                                fallback={<Loader message="Checking session" />}
                            >
                                <OnboardingPage />
                            </Suspense>
                        ),
                    },
                    {
                        path: appRoutes.AUTH_SETUP_CHECKING,
                        element: (
                            <Suspense
                                fallback={<Loader message="Checking session" />}
                            >
                                <CheckingPage />
                            </Suspense>
                        ),
                    },
                    {
                        path: appRoutes.AUTH_SETUP_DONE,
                        element: (
                            <Suspense
                                fallback={<Loader message="Checking session" />}
                            >
                                <DonePage />
                            </Suspense>
                        ),
                    },

                    {
                        path: appRoutes.INVITE,
                        element: <AcceptInvite />,
                    },
                ],
            },

            // * Public operator/app routes
            {
                path: '',
                element: <OperatorLayout />,
                errorElement: <ErrorPage />,
                children: [
                    {
                        path: '',
                        index: true,
                        element: <Navigate to="home" />,
                    },
                    { path: 'home', element: <SignIn /> },
                    {
                        path: appRoutes.AUTH_LOGIN,
                        element: (
                            <Suspense
                                fallback={
                                    <Loader message="Getting all your organizations" />
                                }
                            >
                                <LoginAuthPage />
                            </Suspense>
                        ),
                        children: [],
                    },
                    {
                        path: appRoutes.LOGGED_OUT,
                        element: <LoggedOut />,
                    },
                    {
                        path: appRoutes.INVITE_SIGN_UP,
                        element: (
                            <LoginPage
                                params={{
                                    appState: {
                                        targetUrl:
                                            appRoutes.AUTH_SETUP_ONBOARDING,
                                    },
                                }}
                                action="invite"
                            />
                        ),
                    },
                    {
                        path: appRoutes.RESET_PASSWORD,
                        element: <ResetPasswordPage />,
                    },
                    { path: 'home', element: <SignIn /> },
                    { index: true, element: <Navigate to="home" /> },
                ],
            },
        ],
    },

    // * Public routes
    {
        path: appRoutes.EXTENSION_SIGNIN,
        element: <GoogleSignInLanding />,
    },
];

const sentryCreateBrowserRouter =
    Sentry.wrapCreateBrowserRouterV6(createBrowserRouter);

export default sentryCreateBrowserRouter(routes);
